// Enums baked into the engine. These are NEVER per-vertical — they describe
// the engine's own state machine. Per-vertical lists (categories,
// severities, party roles, etc.) live in the recipe instead.
import { z } from "zod";
// Report lifecycle. v2 simplified from 5 states to 2 (`processing`,
// `completed`, `published` didn't carry enough product weight) — that
// lesson still holds for the wire-level status a client branches on, but
// the DB column has since grown de-facto intermediate values that this
// enum was silently not declaring (found during #0474/#0477's incident
// review): analyzing/analyzed/analysis_failed are set by reports.ts's
// block-complete path while ai-svc runs (apps/core-api/src/routes/reports.ts
// ~L1122-1164, matches packages/api-types/src/asset-groups.ts's
// wireReportStatus, which already modeled these at the wire boundary), and
// discarded closes an orphaned duplicate report (#0474) without pretending
// it was ever signed.
//   in_progress:     report exists, capture is happening, signatures not yet
//   analyzing:       capture complete, ai-svc is processing at least one block
//   analyzed:        ai-svc finished; findings/checklist results are in
//   analysis_failed: ai-svc processing failed; capture flow re-opens for retry
//   signed:          signatures collected; report is closed and shareable
//   discarded:       an orphaned duplicate — never signed, closed explicitly
//   closed:          closed by unilateral attestation, not a signature
//                     (ADR-0098). Declared here so this schema honestly
//                     describes the `reports.status` column's domain, but
//                     it is NOT a terminality signal by itself and no row
//                     carries it yet — POST /reports/:id/close (#0509)
//                     is the only writer that will ever set it, and it
//                     sets closed_at in the same statement. Terminality
//                     is read from `closedAt`, never from this literal —
//                     see isReportTerminal()/reportTerminalKind() in
//                     ./entities/report.ts, which deliberately gain no
//                     "closed" status arm (that would resurrect ADR-0098's
//                     rejected Option H: two competing terminal
//                     predicates).
export const reportStatusSchema = z.enum([
    "in_progress",
    "analyzing",
    "analyzed",
    "analysis_failed",
    "signed",
    "discarded",
    "closed",
]);
// AreaCapture is a buffer for the offline capture flow: photos and audio
// transcripts sit here while the AI processes them into structured findings.
//   pending:    captured on device, not yet processed
//   processing: ai-svc is currently working on it
//   done:       findings have been generated and persisted
//   error:      ai-svc failed; client may retry
// area_captures.status as core-api actually writes it (POST /reports/:id/
// blocks/:areaId/complete and the analyze-block consumer): pending →
// analyzing → analyzed | analysis_failed. The four historical literals stay
// accepted so a row written before the rename still parses on the wire;
// nothing in the monorepo branches on them anymore (2026-09-25).
export const areaCaptureStatusSchema = z.enum([
    "pending",
    "analyzing",
    "analyzed",
    "analysis_failed",
    "processing",
    "done",
    "error",
]);
// What kind of file/content an Evidence row points at.
export const evidenceTypeSchema = z.enum(["photo", "audio", "note"]);
//# sourceMappingURL=enums.js.map